import { useEffect, useState } from 'react';
import {
  gravarRnDaCampanhaSpt, gravarRnDaOs, lerNumero, metro, verRnDaCampanhaSpt, verRnDaOs, type ReferenciaNivel,
} from '../api/spt';
import { Carregando, ErroDaApi } from '../ui';

/**
 * A referência de nível (RN) da OS — onda 362 (NBR 6484:2020, 5.2.1 e 7.2.2 b).
 *
 * «Isso é interessante para todos os ensaios de campo» (Humberto, 01/11/2026): uma RN por OS — o elemento
 * físico tomado como RN, a cota (georreferenciada, ou arbitrária fora do perímetro da obra: guia, calçada) e,
 * se houver, a posição. A planta de locação do SPT e do CPTu a imprime, e as cotas a cada 5 m do boletim SPT
 * saem dela. O SPT não vai à verificação sem RN.
 */
export function ReferenciaNivelDaOs({ osId, podeEditar, campanhaSpt, aoGravar }: {
  osId: string; podeEditar: boolean;
  /** 366: dentro da campanha SPT, a RN vai pela rota do SPT (quem lança o SPT lança a RN). */
  campanhaSpt?: string;
  aoGravar?(): void;
}) {
  const ler = () => (campanhaSpt ? verRnDaCampanhaSpt(campanhaSpt) : verRnDaOs(osId));
  const escrever = (corpo: Partial<ReferenciaNivel>) => (campanhaSpt ? gravarRnDaCampanhaSpt(campanhaSpt, corpo) : gravarRnDaOs(osId, corpo));
  const [rn, setRn] = useState<ReferenciaNivel | null | undefined>(undefined);
  const [f, setF] = useState({ descricao: '', cota: '', tipo: 'arbitraria', latitude: '', longitude: '' });
  const [erro, setErro] = useState<unknown>(null);
  const [gravado, setGravado] = useState(false);
  const preencher = (x: ReferenciaNivel | null) => {
    setRn(x);
    setF({
      descricao: x?.descricao ?? '', cota: x ? metro(x.cota, 3) : '', tipo: x?.tipo ?? 'arbitraria',
      latitude: x?.latitude == null ? '' : String(x.latitude).replace('.', ','),
      longitude: x?.longitude == null ? '' : String(x.longitude).replace('.', ','),
    });
  };
  useEffect(() => { ler().then((r) => preencher(r.rn)).catch(setErro); }, [osId, campanhaSpt]); // eslint-disable-line react-hooks/exhaustive-deps
  if (rn === undefined) return erro ? <ErroDaApi erro={erro} /> : <Carregando texto="Carregando a referência de nível" />;

  const gravar = async () => {
    setErro(null); setGravado(false);
    try {
      const r = await escrever({
        descricao: f.descricao.trim(), cota: lerNumero(f.cota) ?? undefined, tipo: f.tipo as ReferenciaNivel['tipo'],
        latitude: lerNumero(f.latitude), longitude: lerNumero(f.longitude),
      });
      preencher(r.rn); setGravado(true); aoGravar?.();
    } catch (e) { setErro(e); }
  };
  const id = (s: string) => `rn-${s}`;

  return (
    <div className="cartao rn-da-os">
      <div className="cartao-cabeca">
        <h2>Referência de nível (RN)</h2>
        <span className="sub cresce">{rn ? `cota ${metro(rn.cota, 3)} m · ${rn.tipo === 'georreferenciada' ? 'georreferenciada' : 'arbitrária'}` : 'não cadastrada'}</span>
      </div>
      <p className="sub">
        O elemento físico usado no nivelamento das bocas dos furos (NBR 6484, 5.2.1). Sem RN do contratante, adote uma arbitrária
        fora do perímetro da obra (guia, calçada). Vale para todos os ensaios de campo desta OS e sai na planta de locação.
      </p>
      <ErroDaApi erro={erro} />
      <form className="rn-grade" onSubmit={(e) => { e.preventDefault(); void gravar(); }}>
        <fieldset disabled={!podeEditar} className="rn-campos">
          <label className="rn-largo" htmlFor={id('descricao')}>Elemento físico
            <input id={id('descricao')} className="campo" value={f.descricao} maxLength={300}
              placeholder="ex.: topo da guia em frente ao lote 62" onChange={(e) => setF({ ...f, descricao: e.target.value })} />
          </label>
          <label htmlFor={id('cota')}>Cota (m)
            <input id={id('cota')} className="campo" inputMode="decimal" value={f.cota} onChange={(e) => setF({ ...f, cota: e.target.value })} />
          </label>
          <label htmlFor={id('tipo')}>Tipo
            <select id={id('tipo')} className="campo" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>
              <option value="arbitraria">Arbitrária</option>
              <option value="georreferenciada">Georreferenciada</option>
            </select>
          </label>
          <label htmlFor={id('lat')}>Latitude (opcional)
            <input id={id('lat')} className="campo" inputMode="decimal" value={f.latitude} placeholder="-26,9xxxxx"
              onChange={(e) => setF({ ...f, latitude: e.target.value })} />
          </label>
          <label htmlFor={id('lon')}>Longitude (opcional)
            <input id={id('lon')} className="campo" inputMode="decimal" value={f.longitude} placeholder="-49,0xxxxx"
              onChange={(e) => setF({ ...f, longitude: e.target.value })} />
          </label>
        </fieldset>
        {podeEditar && (
          <div className="rn-acoes">
            <button type="submit" className="botao secundario">Gravar a RN</button>
            {gravado && <span className="sub" role="status">Gravada.</span>}
          </div>
        )}
      </form>
    </div>
  );
}
